'use client';

import { motion, useReducedMotion } from 'framer-motion';
import { Check, Navigation, Sparkles } from 'lucide-react';

function WhatsAppGlyph({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

/** Pop entrance with overshoot, per the system's "elements pop in" rule. */
const pop = (delay: number, reduced: boolean | null) =>
  reduced
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { duration: 0.2 } }
    : {
        initial: { opacity: 0, scale: 0.8, y: 16 },
        animate: { opacity: 1, scale: 1, y: 0 },
        transition: { duration: 0.5, delay, ease: [0.34, 1.56, 0.64, 1] as const },
      };

export default function HeroIllustration() {
  const reduced = useReducedMotion();

  return (
    <div className="relative mx-auto w-full max-w-[540px]">
      <div className="relative space-y-4">
        {/* ── Platform: the mission board ── */}
        <motion.div {...pop(0.05, reduced)}>
          <div className="rounded-2xl border-2 border-pop-ink bg-card p-5 shadow-pop-lg">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] font-extrabold uppercase tracking-[0.15em] text-accent">
                  Mission 402
                </p>
                <p className="mt-0.5 font-outfit text-base font-bold">Midtown afternoon run</p>
              </div>
              <span className="flex items-center gap-1.5 rounded-full border-2 border-pop-ink bg-pop-mint px-2.5 py-1 text-[10px] font-extrabold uppercase text-pop-ink">
                <span className="h-1.5 w-1.5 rounded-full bg-pop-ink" />
                Live
              </span>
            </div>

            {/* Route */}
            <div className="relative mt-4 overflow-hidden rounded-xl border-2 border-pop-ink bg-muted">
              <div className="bg-line-grid h-24 w-full">
                <svg viewBox="0 0 300 96" className="h-24 w-full" fill="none">
                  <path
                    d="M24 72 C 72 72, 80 24, 128 24 S 196 68, 246 44 L 276 28"
                    stroke="hsl(var(--pop-ink))"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeDasharray="7 8"
                    opacity="0.25"
                  />
                  <motion.path
                    d="M24 72 C 72 72, 80 24, 128 24 S 196 68, 246 44 L 276 28"
                    stroke="hsl(var(--accent))"
                    strokeWidth="4"
                    strokeLinecap="round"
                    initial={{ pathLength: reduced ? 1 : 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 2, delay: 0.6, ease: 'easeInOut' }}
                  />
                  {[
                    [24, 72],
                    [128, 24],
                    [246, 44],
                  ].map(([cx, cy], i) => (
                    <circle
                      key={i}
                      cx={cx}
                      cy={cy}
                      r="6"
                      fill="white"
                      stroke="hsl(var(--pop-ink))"
                      strokeWidth="3"
                    />
                  ))}
                  <circle cx="276" cy="28" r="8" fill="hsl(var(--pop-pink))" stroke="hsl(var(--pop-ink))" strokeWidth="3" />
                </svg>
              </div>
            </div>

            {/* Crew */}
            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="flex -space-x-2">
                  {['bg-accent', 'bg-pop-pink', 'bg-pop-amber'].map((tone, i) => (
                    <span
                      key={i}
                      className={`h-7 w-7 rounded-full border-2 border-pop-ink ${tone}`}
                    />
                  ))}
                </div>
                <span className="text-xs font-bold text-muted-foreground">3 joined</span>
              </div>
              <span className="text-xs font-extrabold">12 stops</span>
            </div>
          </div>
        </motion.div>

        {/* ── App + WhatsApp side by side ── */}
        <div className="grid grid-cols-5 gap-4">
          {/* Logistr One */}
          <motion.div {...pop(0.2, reduced)} className="col-span-2">
            <div className="h-full rounded-2xl border-2 border-pop-ink bg-pop-ink p-1.5 shadow-pop-lg">
              <div className="h-full rounded-xl bg-card p-3">
                <div className="flex items-center justify-between">
                  <span className="text-[9px] font-extrabold uppercase tracking-wider text-muted-foreground">
                    Logistr One
                  </span>
                  <span className="h-2 w-2 rounded-full bg-pop-mint" />
                </div>

                <p className="mt-2 font-outfit text-sm font-bold leading-tight">Stop 4 of 12</p>
                <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">
                  480 Oak Street, Apt 3B
                </p>

                <div className="mt-3 flex items-center justify-center gap-1.5 rounded-full border-2 border-pop-ink bg-accent px-2 py-1.5 text-white">
                  <Navigation className="h-3 w-3" strokeWidth={2.5} />
                  <span className="text-[10px] font-extrabold">Navigate</span>
                </div>

                <div className="mt-2 flex items-center gap-1.5 rounded-lg border-2 border-dashed border-border px-2 py-1.5">
                  <Check className="h-3 w-3 text-pop-mint" strokeWidth={3} />
                  <span className="text-[10px] font-bold text-muted-foreground">Photo proof</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* WhatsApp */}
          <motion.div {...pop(0.35, reduced)} className="col-span-3">
            <div className="h-full rounded-2xl border-2 border-pop-ink bg-card p-4 shadow-pop-lg">
              <div className="flex items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-pop-ink bg-[#25D366] text-white">
                  <WhatsAppGlyph className="h-3 w-3" />
                </span>
                <span className="text-xs font-extrabold">WhatsApp</span>
              </div>

              <div className="mt-3 space-y-2">
                <div className="w-fit max-w-[92%] rounded-blob border-2 border-pop-ink bg-muted px-3 py-1.5 text-[11px] font-medium leading-snug">
                  Where do I go next?
                </div>
                <motion.div
                  initial={{ opacity: 0, x: reduced ? 0 : 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.2, duration: 0.4 }}
                  className="ml-auto w-fit max-w-[94%] rounded-2xl rounded-br-none border-2 border-pop-ink bg-accent px-3 py-1.5 text-[11px] font-medium leading-snug text-white"
                >
                  480 Oak St, Apt 3B. Sent to your app.
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* AI badge ties the three surfaces together */}
      <motion.div
        {...pop(0.5, reduced)}
        className="absolute -right-2 top-1/2 z-10 -translate-y-1/2 sm:-right-6"
      >
        <div className="flex items-center gap-1.5 rounded-full border-2 border-pop-ink bg-pop-amber px-3 py-2 shadow-pop">
          <Sparkles className="h-4 w-4 text-pop-ink" strokeWidth={2.5} />
          <span className="text-[11px] font-extrabold text-pop-ink">AI assistant</span>
        </div>
      </motion.div>
    </div>
  );
}
